import React, { useEffect } from "react";
import SEO from "../components/SEO";

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      <SEO
        title="Privacy Policy"
        description="Learn how Trademark Facility collects, uses, and protects your personal information."
        path="/privacy"
      />
      {/* Hero */}
      <section className="relative bg-[#07382f] text-white py-12 md:py-16 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h1 className="font-heading text-3xl md:text-5xl font-extrabold mb-3 tracking-tight">
            Privacy Policy
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">
            How we collect, use, and protect your personal information.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-10 md:py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-slate max-w-none">
          <p className="text-slate-600 leading-relaxed mb-6 text-sm sm:text-base">
            At Trademark Facility we are committed to protecting your privacy
            and ensuring that your personal information is handled in a safe and
            responsible manner. This Privacy Policy outlines how we collect,
            use, and protect your personal data, and it applies to all
            interactions with our website, products, and services.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Information We Collect
          </h2>
          <p className="text-slate-600 mb-4">
            We collect personal information from you when you visit our website,
            register for an account, make a purchase, or communicate with us.
            The types of information we may collect include:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-3 mb-6">
            <li>
              <strong>Personal Identification Information:</strong> Name, email
              address, phone number, billing and shipping addresses, and payment
              information.
            </li>
            <li>
              <strong>Non-personal Identification Information:</strong> IP
              address, browser type, operating system, and website usage
              information.
            </li>
            <li>
              <strong>Cookies and Tracking Technologies:</strong> We use cookies
              and similar tracking technologies to enhance your browsing
              experience, analyze website traffic, and personalize content.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            How We Use Your Information
          </h2>
          <p className="text-slate-600 mb-4">
            We use the information we collect for various purposes, including:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6">
            <li>To process and manage your transactions and orders.</li>
            <li>
              To communicate with you about your orders, services, or updates.
            </li>
            <li>To respond to your inquiries and provide customer support.</li>
            <li>To improve our website, products, and services.</li>
            <li>
              To send promotional materials, newsletters, and other marketing
              communications (if you have opted-in to receive them).
            </li>
            <li>To prevent fraud and ensure the security of our website.</li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            How We Protect Your Information
          </h2>
          <p className="text-slate-600 mb-4">
            We implement a variety of security measures to maintain the safety
            of your personal information, including:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-3 mb-6">
            <li>
              <strong>Encryption:</strong> We use SSL encryption technology to
              protect sensitive data during transmission.
            </li>
            <li>
              <strong>Secure Storage:</strong> Your personal information is
              stored in a secure environment with restricted access.
            </li>
            <li>
              <strong>Regular Audits:</strong> We regularly review our security
              practices to ensure that your data is safeguarded.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Sharing Your Information
          </h2>
          <p className="text-slate-600 mb-4">
            We do not sell, trade, or otherwise transfer your personal
            information to third parties, except in the following cases:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-3 mb-6">
            <li>
              <strong>Service Providers:</strong> We may share your data with
              trusted third-party service providers who assist us in operating
              our website, processing payments, or providing customer service.
              These providers are obligated to maintain the confidentiality and
              security of your information.
            </li>
            <li>
              <strong>Legal Compliance:</strong> We may disclose your
              information to comply with legal obligations, enforce our site
              policies, or protect our rights or the rights of others.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Your Rights and Choices
          </h2>
          <p className="text-slate-600 mb-4">
            You have the following rights regarding your personal information:
          </p>
          <ul className="list-disc pl-6 text-slate-600 space-y-3 mb-6">
            <li>
              <strong>Access and Correction:</strong> You may request access to
              your personal information and request corrections or updates to
              your data.
            </li>
            <li>
              <strong>Opt-Out:</strong> If you no longer wish to receive
              marketing communications from us, you can opt-out by clicking the
              unsubscribe link in any email or by contacting us directly.
            </li>
            <li>
              <strong>Deletion:</strong> You may request that we delete your
              personal information, subject to applicable legal and regulatory
              obligations.
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Cookies and Tracking Technologies
          </h2>
          <p className="text-slate-600 mb-6">
            We use cookies and similar technologies to enhance your experience
            on our website. You can choose to disable cookies through your
            browser settings, but this may affect your ability to access certain
            features of our website.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Data Retention
          </h2>
          <p className="text-slate-600 mb-6">
            We retain your personal information for as long as necessary to
            fulfill the purposes for which it was collected, including legal,
            accounting, or reporting requirements.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Third-Party Links
          </h2>
          <p className="text-slate-600 mb-6">
            Our website may contain links to third-party websites. We are not
            responsible for the privacy practices of these external sites, and
            we encourage you to review their privacy policies before providing
            any personal information.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Children's Privacy
          </h2>
          <p className="text-slate-600 mb-6">
            Our website is not intended for children under the age of 13. We do
            not knowingly collect or solicit personal information from children.
            If we learn that we have inadvertently collected such information,
            we will delete it as soon as possible.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Changes to This Privacy Policy
          </h2>
          <p className="text-slate-600 mb-6">
            We reserve the right to modify or update this Privacy Policy at any
            time. Any changes will be posted on this page with an updated
            "Effective Date." We encourage you to review this policy
            periodically to stay informed about how we are protecting your
            information.
          </p>

          <h2 className="text-2xl font-bold text-slate-900 mt-10 mb-4">
            Contact Us
          </h2>
          <p className="text-slate-600 mb-2">
            If you have any questions or concerns about this Privacy Policy or
            how we handle your personal information, please contact us at:
          </p>
          <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 mb-6">
            <p className="text-slate-700 mb-1">
              <strong>Company:</strong> Trademark Facility
            </p>
            <p className="text-slate-700 mb-1">
              <strong>Address:</strong> 340 E Middlefield Rd, Mountain View, CA
              94043
            </p>
            <p className="text-slate-700">
              <strong>Email:</strong> info@trademarkfacility.com
            </p>
          </div>

          <p className="text-slate-600">
            By using our website and services, you consent to the terms of this
            Privacy Policy.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
